import React, { useState } from 'react';
import { 
    getAuth,
    signInWithEmailAndPassword, 
    createUserWithEmailAndPassword, 
    GoogleAuthProvider, 
    signInWithPopup, 
    signInAnonymously, 
    fetchSignInMethodsForEmail, 
    linkWithCredential, 
    EmailAuthProvider, 
    sendPasswordResetEmail 
} from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp, getFirestore } from 'firebase/firestore';
import { initializeApp } from 'firebase/app';
import { auth, db } from '../../config/firebase'; 

export default function AuthModal({ initialForm, onClose, navigateTo, preAuthPageInfo }) {
    const [form, setForm] = useState(initialForm || 'login');

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4" onClick={onClose}>
            <div className="relative w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                <button onClick={onClose} className="absolute -top-4 -right-4 bg-white rounded-full p-1 z-10 shadow-sm hover:bg-gray-100 transition">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
                <AuthForm
                    isLogin={form === 'login'}
                    navigateTo={navigateTo}
                    preAuthPageInfo={preAuthPageInfo}
                    onSuccess={onClose}
                    onSwitchForm={() => setForm(form === 'login' ? 'signup' : 'login')}
                />
            </div>
        </div>
    );
}

function AuthForm({ isLogin, navigateTo, preAuthPageInfo, onSuccess, onSwitchForm }) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [agreeTerms, setAgreeTerms] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [isResetView, setIsResetView] = useState(false);
    const [resetMessage, setResetMessage] = useState('');

    const handleSuccess = () => {
        onSuccess();
        if (preAuthPageInfo && preAuthPageInfo.page && preAuthPageInfo.page !== 'home') {
            navigateTo && navigateTo(preAuthPageInfo.page, preAuthPageInfo.data);
        } else {
            navigateTo && navigateTo('home');
        }
    };

    const handlePasswordReset = async (e) => {
        e.preventDefault();
        setError('');
        setResetMessage('');
        if (!email) {
            setError("Please enter your email address to reset your password.");
            return;
        }
        try {
            await sendPasswordResetEmail(auth, email);
            setResetMessage("Password reset link sent! Please check your email inbox.");
        } catch (err) {
            setError(err.message);
        }
    };

    const handleEmailPassword = async (e) => {
        e.preventDefault(); 
        setError('');
        
        if (!isLogin && !agreeTerms) {
            setError("You must agree to the Terms of Service and Privacy Policy to create an account.");
            return;
        }

        try {
            if (isLogin) {
                await signInWithEmailAndPassword(auth, email, password);
            } else {
                const methods = await fetchSignInMethodsForEmail(auth, email);
                if (methods.length > 0) {
                    setError("This email address is already in use. Please log in.");
                    return;
                }

                const credential = EmailAuthProvider.credential(email, password);
                const currentUser = auth.currentUser;
                let finalUser;

                if (currentUser && currentUser.isAnonymous) {
                    const userCredential = await linkWithCredential(currentUser, credential);
                    finalUser = userCredential.user;
                } else {
                    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
                    finalUser = userCredential.user;
                }
                
                if (finalUser) {
                    await finalUser.getIdToken(true);
                }

                await setDoc(doc(db, "users", finalUser.uid), {
                    uid: finalUser.uid,
                    email: finalUser.email,
                    isVendor: false,
                    createdAt: serverTimestamp(),
                    favorites: []
                });
            }
            handleSuccess();
        } catch (err) { 
            setError(err.message); 
        }
    };

    const handleGoogleSignIn = async () => {
        const provider = new GoogleAuthProvider(); 
        setError('');
        
        try {
            const result = await signInWithPopup(auth, provider);
            await result.user.getIdToken(true);
            
            const user = result.user;
            const userDocRef = doc(db, "users", user.uid);
            const docSnap = await getDoc(userDocRef);
            
            if (!docSnap.exists()) {
                await setDoc(userDocRef, {
                    uid: user.uid, 
                    email: user.email,
                    isVendor: false,
                    createdAt: serverTimestamp(),
                    favorites: []
                });
            } 
            handleSuccess();
        } catch (err) { 
            setError(err.message); 
        }
    };

    const handleGuestSignIn = async () => {
        setError('');
        try {
            await signInAnonymously(auth);
            onSuccess();
        } catch (err) {
            console.error("Guest sign-in failed:", err);
            setError("Guest checkout is currently unavailable. Please sign in or create an account.");
        }
    };

    const title = isLogin ? 'Login' : 'Create an Account';

    if (isResetView) {
        return (
            <div className="max-w-md mx-auto bg-white p-6 md:p-8 rounded-lg shadow-lg">
                <h2 className="text-xl md:text-2xl font-bold text-center mb-6 text-gray-900">Reset Password</h2>
                {error && <p className="text-red-600 font-semibold text-center mb-4 bg-red-50 p-3 rounded-lg border border-red-100">{error}</p>}
                {resetMessage && <p className="text-green-600 font-semibold text-center mb-4 bg-green-50 p-3 rounded-lg border border-green-100">{resetMessage}</p>}
                
                <form onSubmit={handlePasswordReset} className="space-y-4">
                    <input 
                        type="email" 
                        placeholder="Enter your email" 
                        value={email} 
                        onChange={e => setEmail(e.target.value)} 
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none" 
                        required 
                    />
                    <button type="submit" className="w-full bg-pink-500 text-white py-3 rounded-lg font-semibold hover:bg-pink-600 transition shadow-md">
                        Send Reset Link
                    </button>
                </form>
                
                <p className="text-center mt-6">
                    <button onClick={() => setIsResetView(false)} className="text-pink-600 font-semibold cursor-pointer hover:underline transition">
                        Back to Login
                    </button>
                </p>
            </div>
        );
    }

    return (
        <div className="max-w-md mx-auto bg-white p-6 md:p-8 rounded-xl shadow-lg border border-gray-100">
            <h2 className="text-2xl font-bold text-center mb-6 text-gray-900">{title}</h2>
            
            {error && <p className="text-red-600 font-semibold text-center mb-4 bg-red-50 p-3 rounded-lg border border-red-100">{error}</p>}
            
            <form onSubmit={handleEmailPassword} className="space-y-4">
                <input 
                    type="email" 
                    placeholder="Email" 
                    value={email} 
                    onChange={e => setEmail(e.target.value)} 
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none" 
                    required 
                />
                <div className="relative">
                    <input 
                        type={showPassword ? "text" : "password"} 
                        placeholder="Password" 
                        value={password} 
                        onChange={e => setPassword(e.target.value)} 
                        className="w-full p-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none" 
                        required 
                    />
                    <button 
                        type="button" 
                        onClick={() => setShowPassword(!showPassword)} 
                        className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-pink-600 focus:outline-none font-semibold text-sm"
                    >
                        {showPassword ? "Hide" : "Show"}
                    </button>
                </div>
                
                {!isLogin && (
                    <div className="flex items-start bg-gray-50 p-3 rounded-lg border border-gray-100 mt-2">
                        <div className="flex items-center h-5 mt-0.5">
                            <input 
                                id="terms" 
                                type="checkbox" 
                                checked={agreeTerms} 
                                onChange={(e) => setAgreeTerms(e.target.checked)} 
                                className="focus:ring-pink-500 h-4 w-4 text-pink-600 border-gray-300 rounded cursor-pointer" 
                            />
                        </div>
                        <div className="ml-3 text-xs leading-relaxed text-gray-600">
                            <label htmlFor="terms" className="cursor-pointer">
                                I agree to the <a href="#terms-of-service" className="text-pink-600 hover:underline font-bold" onClick={(e)=> {e.stopPropagation();}}>Terms of Service</a> and <a href="#privacy-policy" className="text-pink-600 hover:underline font-bold" onClick={(e)=> {e.stopPropagation();}}>Privacy Policy</a>.
                            </label>
                        </div>
                    </div>
                )}

                {isLogin && (
                    <div className="text-right mt-1">
                        <button type="button" onClick={() => setIsResetView(true)} className="text-sm font-semibold text-pink-600 hover:underline transition">
                            Forgot Password?
                        </button>
                    </div>
                )}
                
                <button type="submit" className="w-full bg-pink-600 text-white py-3.5 rounded-lg font-bold hover:bg-pink-700 transition shadow-md mt-2">
                    {title}
                </button>
            </form>
            
            <p className="text-center mt-5 text-sm text-gray-600">
                {isLogin ? "Don't have an account?" : "Already have an account?"} 
                <button onClick={onSwitchForm} className="text-pink-600 font-bold hover:underline ml-1.5 transition">
                    {isLogin ? "Sign Up" : "Login"}
                </button>
            </p>

            <div className="my-6 flex items-center">
                <div className="flex-grow border-t border-gray-200"></div>
                <span className="flex-shrink mx-4 text-xs font-bold text-gray-400 uppercase tracking-widest">OR</span>
                <div className="flex-grow border-t border-gray-200"></div>
            </div>
            
            <div className="space-y-3">
                <button onClick={handleGuestSignIn} className="w-full bg-white border-2 border-gray-200 text-gray-700 py-3 rounded-lg font-bold hover:bg-gray-50 transition shadow-sm">
                    Continue as Guest
                </button>
                <button onClick={handleGoogleSignIn} className="w-full flex items-center justify-center gap-2 bg-white border-2 border-gray-200 text-gray-700 py-3 rounded-lg font-bold hover:bg-gray-50 transition shadow-sm">
                    <svg className="w-5 h-5" viewBox="0 0 48 48">
                        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.42-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
                        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                        <path fill="none" d="M0 0h48v48H0z"></path>
                    </svg>
                    Continue with Google
                </button>
            </div>
            
            {!isLogin && (
                <p className="text-[10px] text-gray-500 text-center mt-5 px-4 leading-relaxed">
                    By clicking "Sign Up" or "Continue with Google", you agree to our Terms of Service and Privacy Policy.
                </p>
            )}
        </div>
    );
}